import { Injectable, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatLegacyDialog } from '@angular/material/legacy-dialog';
import { combineLatest, Observable, Subject, take, takeUntil } from 'rxjs';

import { formatQuantity } from '@app/shared/utils/number.utils';
import { inline_ } from '@app/shared/utils/string.utils';
import { DialogConfig } from 'v2/modules/shared/configs/dialog.config';
import { UiConfirmationDialogComponent } from 'v2/modules/ui/components/ui-confirmation-dialog/ui-confirmation-dialog.component';
import { IUiConfirmationDialogTextResult } from 'v2/modules/ui/components/ui-confirmation-dialog/ui-confirmation-dialog.component.types';

import { ICostLayer } from '../cost-layers.types';

import { CostLayerEditDataStore } from './cost-layer-edit.data-store';

export interface ICostLayerEditHelperDone {
  didEdit :boolean;
  updatedCostLayer ?:ICostLayer;
}

/**
 * Be sure to add BOTH `CostLayerEditHelperService` AND `CostLayerEditDataStore`
 * to the component's `providers` array.
 *
 * @example
 *   @Component({
 *     selector: 'lum-foo',
 *     templateUrl: './foo.component.html',
 *     styleUrls: ['./foo.component.scss'],
 *     providers: [CostLayerEditDataStore, CostLayerEditHelperService],
 *   })
 *   export class FooComponent {
 *     constructor(
 *       public editHelper :CostLayerEditHelperService,
 *     ) { }
 *   }
 *
 */
@Injectable()
export class CostLayerEditHelperService implements OnDestroy {
  readonly #destroy$ = new Subject<null>();
  readonly #done$ = new Subject<ICostLayerEditHelperDone>();

  #isResettingFromCostLayer :boolean = false;

  readonly done$ :Observable<ICostLayerEditHelperDone> = this.#done$.asObservable();

  readonly NUMBER_PATTERN = '^[0-9]+(\\.[0-9]+)?$';

  form :FormGroup;

  constructor(
    private _legacyDialog :MatLegacyDialog,
    private _fb :FormBuilder,
    public store :CostLayerEditDataStore,
  ) {
    this.#initForm();
  }

  ngOnDestroy() {
    this.#done$.complete();
    this.#destroy$.next(null);
    this.#destroy$.complete();
  }

  isEditingLayer(id :ICostLayer['id']) :boolean {
    return this.store.get('costLayer')?.id === id;
  }

  revertEdits() {
    const costLayer = this.store.get('costLayer');

    this.#resetFormFromCostLayer(costLayer);
    this.setLayer(costLayer);
  }

  setLayer(layer :ICostLayer, options ?:{ loading :boolean }) {
    this.store.setLayer(layer);
  }

  save() {
    if (this.form.pristine) {
      // #sanitycheck: don't save if we haven't edited
      return;
    }

    this._legacyDialog
      .open(UiConfirmationDialogComponent, {
        ...DialogConfig.SMALL,
        data: {
          title: 'Update Cost Layer',
          message: inline_`Attention: Changing the cost layer can impact
            financial records and historical data integrity. Verify that this
            update is necessary and that you’ve considered potential effects on
            pricing and compliance.`,
          input: {
            type: 'textarea',
            label: 'Comment',
            maxLength: 255,
            placeholder: 'Why are you making this change?',
          },
        },
      })
      .afterClosed()
      .pipe(takeUntil(this.#destroy$))
      .subscribe((result :IUiConfirmationDialogTextResult) => {
        if (result && !result.dismiss) {
          this.form.get('comment').setValue(result.value);

          this.store
            .save(this.form.value)
            .pipe(take(1))
            .subscribe((response) => {
              this.#done$.next({
                didEdit: true,
                updatedCostLayer: response.data,
              });
            });
        }
      });
  }

  #updateComputedTotalCost(options ?:{
    costPerUnit ?:string | number;
    initialQuantity ?:string | number;
  }) {
    const unitCost = options?.costPerUnit ?? this.form.get('costPerUnit').value;
    const qty = options?.initialQuantity ?? this.form.get('initialQuantity').value;

    this.store.updateState({ computedTotalCost: unitCost * qty });
  }

  #initForm() {
    this.form = this._fb.group({
      comment: [''], // ICostLayerChange.remarks
      description: [''], // ICostLayer.description
      costPerUnit: [
        '',
        [Validators.required, Validators.pattern(this.NUMBER_PATTERN)],
      ],
      initialQuantity: [
        '',
        [Validators.required, Validators.pattern(this.NUMBER_PATTERN)],
      ],
      remainingQuantity: [
        '',
        [Validators.required, Validators.pattern(this.NUMBER_PATTERN)],
      ],
    });

    // Patch the form when the store.costLayer object changes.
    this.store.costLayer$
      .pipe(takeUntil(this.#destroy$))
      .subscribe((costLayer :ICostLayer) => {
        this.#resetFormFromCostLayer(costLayer);
      });

    // Calculate the total cost when the relevant values change
    combineLatest([
      this.form.get('costPerUnit').valueChanges,
      this.form.get('initialQuantity').valueChanges,
    ])
      .pipe(takeUntil(this.#destroy$))
      .subscribe(([costPerUnit, initialQuantity]) => {
        // updating from the cost layer sets totalCost from the layer data, so
        // we don't need to recompute it here
        if (this.#isResettingFromCostLayer) {
          return;
        }

        this.#updateComputedTotalCost({ costPerUnit, initialQuantity });
      });
  }

  #prepCurrencyValue(value :string | number | null) :string {
    if (value == null) {
      return '';
    }

    value = value.toString();

    const numeric = parseFloat(value.replace(/,/g, ''));

    if (Number.isNaN(numeric)) {
      return value;
    }

    return formatQuantity(numeric, { digits: '1.2-6' }).replace(/,|\.00$/g, ''); // strip commas and trailing .00 cents.
  }
  #prepQuantityValue(value :string | number | null) :string {
    if (value == null) {
      return '';
    }

    value = value.toString();

    const numeric = parseFloat(value.replace(/,/g, ''));

    if (Number.isNaN(numeric)) {
      return value;
    }

    return formatQuantity(numeric).replace(/,/g, ''); // strip commas
  }

  #resetFormFromCostLayer(costLayer :ICostLayer) {
    this.#isResettingFromCostLayer = true;
    this.form.reset({
      comment: '',
      description: costLayer?.description ?? '',
      costPerUnit: this.#prepCurrencyValue(costLayer?.costPerUnit),
      initialQuantity: this.#prepQuantityValue(costLayer?.initialQuantity),
      remainingQuantity: this.#prepQuantityValue(costLayer?.remainingQuantity),
    });
    this.#isResettingFromCostLayer = false;
  }
}
